import { useState, useEffect } from "react";
import { useLocation } from "react-router";
import { ChevronRight } from "lucide-react";
import type { NavigationItemData } from "@/features/navigation/types/navigation.type";
import { SidebarMotionLink } from "@/shared/components/SidebarMotionLink";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/shared/components/ui/collapsible";
import {
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
} from "@/shared/components/ui/sidebar";
import { cn } from "@/shared/utils/style.util";

type SidebarCollapsibleItemProps = {
  item: NavigationItemData
  onMainNavigationClose?: () => void
};

function SidebarCollapsibleItem({
  item,
  onMainNavigationClose,
}: SidebarCollapsibleItemProps) {
  const location = useLocation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [userClosed, setUserClosed] = useState(false);
  const IconComponent = item.icon;

  const hasActiveChild =
    item.children?.some((child) => location.pathname === child.to) ?? false;

  useEffect(() => {
    if (hasActiveChild && !isExpanded && !userClosed) {
      setIsExpanded(true);
    }
  }, [hasActiveChild, isExpanded, userClosed]);

  const handleOpenChange = (open: boolean) => {
    setIsExpanded(open);
    if (!open) {
      setUserClosed(true);
    }
    else {
      setUserClosed(false);
    }
  };

  return (
    <Collapsible open={isExpanded} onOpenChange={handleOpenChange}>
      <SidebarMenuItem>
        <CollapsibleTrigger asChild>
          <SidebarMenuButton
            className="hover:bg-sidebar-primary/10 hover:text-sidebar-primary dark:hover:bg-sidebar-primary/15 [&>span:last-child]:overflow-visible [&>span:last-child]:min-w-5"
            aria-expanded={isExpanded}
          >
            <IconComponent size={20} className="shrink-0" />
            <span className="min-w-0 flex-1 truncate" title={item.label}>{item.label}</span>
            <span className="ml-auto flex shrink-0" aria-hidden>
              <ChevronRight
                size={20}
                className={cn(
                  "transition-transform duration-200",
                  isExpanded && "rotate-90",
                )}
              />
            </span>
          </SidebarMenuButton>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <SidebarMenuSub>
            {item.children?.map((child) => {
              const isChildActive = location.pathname === child.to;

              return (
                <SidebarMenuSubItem key={child.to}>
                  <SidebarMenuSubButton
                    asChild
                    isActive={isChildActive}
                    className="hover:bg-sidebar-primary/10 hover:text-sidebar-primary dark:hover:bg-sidebar-primary/15 data-[active=true]:bg-sidebar-primary data-[active=true]:text-sidebar-primary-foreground"
                  >
                    <SidebarMotionLink
                      to={child.to}
                      onClick={onMainNavigationClose}
                    >
                      <span>{child.label}</span>
                    </SidebarMotionLink>
                  </SidebarMenuSubButton>
                </SidebarMenuSubItem>
              );
            })}
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  );
}

SidebarCollapsibleItem.displayName = "SidebarCollapsibleItem";

export { SidebarCollapsibleItem };
